import { Button } from "@chakra-ui/react";
import type React from "react";
import { useEffect, useMemo } from "react";

import { dayjs } from "@equidefi/i18n";
import { ENTITY, JOINT } from "@equidefi/shared";
import type { DTO } from "@equidefi/shared/types";
import { Text } from "@equidefi/ui";

import { usePlaidEnabled, usePlaidVault } from "hooks/usePlaid";
import { AccreditationFileList } from "./AccreditationFileList";
import { AccreditationMethodSection } from "./AccreditationMethodSection";
import { AccreditationRequirements } from "./AccreditationRequirements";

const ConnectWithPlaidButton = ({ onClick }: { onClick: () => void }) => (
  <Button
    w="full"
    h="56px"
    borderRadius="2xl"
    bgGradient="linear(to-b, #3b6dc4, #234176)"
    color="white"
    fontSize="lg"
    fontWeight="semibold"
    boxShadow="card"
    _hover={{ filter: "brightness(110%)" }}
    _active={{ filter: "brightness(95%)" }}
    onClick={onClick}>
    Connect with Plaid
  </Button>
);

export const PlaidUploadArea: React.FC<{
  refetch?: () => Promise<void> | void;
  isTerminated: boolean;
  vault: DTO.Vault;
  files?: DTO.AccreditationFile[];
  offering?: DTO.Offering;
}> = ({ refetch = () => {}, vault, isTerminated, files, offering }) => {
  const plaidEnabled = usePlaidEnabled(offering);

  const vaultPlaid = usePlaidVault(vault?.id as string, {
    onSuccess: () => {
      refetch();
    },
    onExit: () => {
      refetch();
    },
  });

  useEffect(() => {
    if (vaultPlaid.token && vaultPlaid.link.ready) {
      vaultPlaid.link.open();
    }
  }, [vaultPlaid.link, vaultPlaid.token]);

  const isUsAddress = vault?.address_country_code === "US";
  const isJoint = vault?.account_type === JOINT;
  const isEntity = vault?.entity_type === ENTITY;
  const year = dayjs().year();

  const isPlaidAvailable = useMemo(() => {
    // Plaid is only available to US Individuals and Entities
    if (vault?.citizenship !== "US_CITIZEN") return false;

    // Plaid CRA is available only when enabled AND the primary address is US.
    if (plaidEnabled.cra_enabled && isUsAddress) return true;

    // If Plaid Income is enabled and the vault is an Individual, Plaid is available.
    if (vault?.entity_type === "INDIVIDUAL" && plaidEnabled.income_enabled) return true;
    return false;
  }, [plaidEnabled, vault, isUsAddress]);

  const plaidFiles = useMemo(() => {
    if (!files) return {};
    return {
      plaidAsset: files.filter((file) => file?.name === "Plaid Consumer Report"),
      plaidIncome: files.filter((file) => file?.name === "Plaid Income Results"),
    };
  }, [files]);

  if (!isPlaidAvailable) return null;

  return (
    <>
      {plaidEnabled.cra_enabled && isUsAddress && (
        <AccreditationMethodSection
          title="Verify with Plaid Asset Verification"
          description="Connect financial accounts to verify net worth.">
          <AccreditationRequirements
            summary={
              isEntity
                ? "Connect the entity's bank, brokerage, or other supported financial accounts securely through Plaid to verify its qualifying assets."
                : "Connect your bank, brokerage, or other supported financial accounts securely through Plaid to verify that your net worth exceeds $1,000,000, excluding your primary residence."
            }
            requirements={[
              "Connect enough financial accounts to demonstrate qualifying assets.",
              "All applicable outstanding liabilities must be considered when calculating net worth.",
              "Additional information, including liability documentation, may be required to complete verification.",
            ]}
          />
          <Text fontWeight="bold" fontSize="lg" mb={2}>
            Secure Account Connection
          </Text>
          <Text fontSize="sm" mb={4}>
            Connect the financial accounts needed for verification.
          </Text>
          {!!plaidFiles.plaidAsset?.length && (
            <AccreditationFileList
              noFilesWarning={false}
              vaultId={vault?.id}
              files={plaidFiles.plaidAsset}
              isEditable={false}
              isDeletable={!isTerminated}
              mb={4}
            />
          )}
          <ConnectWithPlaidButton onClick={() => vaultPlaid.createToken()} />
        </AccreditationMethodSection>
      )}

      {vault.entity_type === "INDIVIDUAL" && offering?.plaid_income_enabled && (
        <AccreditationMethodSection
          title="Verify with Plaid Income Verification"
          description="Connect payroll accounts to verify income.">
          <AccreditationRequirements
            summary="Connect your payroll or income accounts securely through Plaid to verify qualifying income for the previous two calendar years."
            requirements={[
              isJoint
                ? `Combined income with your spouse or spousal equivalent exceeding $300,000 in both ${year - 2} and ${year - 1}.`
                : `Income exceeding $200,000 in both ${year - 2} and ${year - 1}.`,
              `You must reasonably expect to meet the same applicable income threshold in ${year}.`,
              "Additional documentation may be required if Plaid cannot retrieve sufficient income history.",
            ]}
          />
          <Text fontWeight="bold" fontSize="lg" mb={2}>
            Secure Account Connection
          </Text>
          <Text fontSize="sm" mb={4}>
            Connect the financial accounts needed for verification.
          </Text>
          {!!plaidFiles.plaidIncome?.length && (
            <AccreditationFileList
              noFilesWarning={false}
              vaultId={vault?.id}
              files={plaidFiles.plaidIncome}
              isEditable={false}
              isDeletable={!isTerminated}
              mb={4}
            />
          )}
          <ConnectWithPlaidButton onClick={() => vaultPlaid.incomeToken()} />
        </AccreditationMethodSection>
      )}
    </>
  );
};
